import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  Body,
  Button,
  Card,
  Checkbox,
  Field,
  I,
  Input,
  LoadingState,
  Row,
  Select,
  Stack,
} from '@leanin/ui';
import { EntryJourneyScreen } from '@/components/entry-journeys';
import { useFinishOnboarding } from '@/components/onboarding/finish-onboarding';
import { PlacePicker, type PlaceSelection } from '@/components/geo';
import { useUpdateMyPrivacy, useUpdateMyProfile } from '@/lib/mutations/identity';
import { useMe } from '@/lib/queries/identity';
import { INDUSTRY_OPTIONS } from '@/lib/member-options';
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepViewed,
} from '@/lib/analytics/events';
import { journeyStepParams } from '@/lib/analytics/onboarding-steps';
import { readEntryJourney } from '@/lib/entry-journey';

// Ad-journey onboarding — the design's minimal "About you" card. Saves
// through the same PATCH /members/me mutation as the organic ProfileStep,
// with the same GDPR marketing opt-in semantics; a direct hit with no
// journey in sessionStorage falls back to the organic flow.
export const Route = createFileRoute('/onboarding/j/about')({
  beforeLoad: () => {
    const journey = readEntryJourney();
    if (journey === null) {
      throw redirect({ to: '/onboarding/profile' });
    }
    return { journey };
  },
  component: JourneyAboutPage,
});

const readString = (form: FormData, key: string): string | null => {
  const value = form.get(key);
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
};

function JourneyAboutPage() {
  const t = useTranslations('EntryJourneys');
  const navigate = useNavigate();
  const { journey } = Route.useRouteContext();
  const updateProfile = useUpdateMyProfile();
  const updatePrivacy = useUpdateMyPrivacy();
  // The join journeys have no completion screen — this step's Continue
  // finishes onboarding directly and the hook lands on the relevant
  // directory (Circles for join-circle, Networks for join-network).
  const finishOnboarding = useFinishOnboarding(
    journey === 'join-network' ? 'li' : 'cm',
  );
  // Prefill from the member row (OAuth signups already carry a name); same
  // swallow-errors-to-defaults contract as /onboarding/profile.
  const { data: me, isPending, isError } = useMe();
  const profile = me !== undefined && !isError ? me : undefined;
  const [place, setPlace] = useState<PlaceSelection | null>(null);
  const [error, setError] = useState<string | null>(null);
  // GDPR Art. 7(2): marketing consent must be a clear affirmative action,
  // not a pre-ticked box. Default OFF; flipping it on writes a
  // `consent_events` row via the privacy update on submit.
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackOnboardingStepViewed(journeyStepParams(journey, '/onboarding/j/about'));
  }, [journey]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const firstName = readString(form, 'firstName');
    if (firstName === null) {
      setError(t('onboarding.about.firstNameError'));
      return;
    }
    const lastName = readString(form, 'lastName');
    if (lastName === null) {
      setError(t('onboarding.about.lastNameError'));
      return;
    }
    const jobTitle = readString(form, 'jobTitle');
    const industry = readString(form, 'industry');
    setError(null);
    updateProfile.mutate(
      {
        firstName,
        lastName,
        jobTitle,
        industry,
        place,
      },
      {
        onSuccess: () => {
          // Fire the privacy update only when the user opted in — the
          // server appends a `consent_events` row (purpose='marketing',
          // GDPR Art. 7(1) demonstrability). Non-blocking: the default is
          // "no marketing" anyway, so a transient failure must not stall
          // the funnel.
          if (marketingOptIn) {
            updatePrivacy.mutate({ marketingOptIn: true });
          }
          trackOnboardingStepCompleted(
            journeyStepParams(journey, '/onboarding/j/about'),
          );
          // start-circle continues into circle setup; the join journeys are
          // done — finish and land on their directory; individual sees the
          // completion step.
          if (journey === 'join-circle' || journey === 'join-network') {
            void finishOnboarding();
            return;
          }
          void navigate({
            to:
              journey === 'start-circle'
                ? '/onboarding/j/circle'
                : '/onboarding/j/youre-in',
          });
        },
        onError: () => {
          setError(t('onboarding.about.saveError'));
        },
      },
    );
  };

  return (
    <EntryJourneyScreen
      journey={journey}
      current="about-you"
    >
      <Card style={{ width: '100%', maxWidth: 440 }}>
        {/* Prefill uses uncontrolled defaultValue, so the form only mounts
          * once the profile fetch settles — mounting empty and letting data
          * arrive later would leave the prefill silently blank. */}
        {isPending ? (
          <LoadingState placement="inset" label={t('steps.aboutYou')} />
        ) : (
        <form onSubmit={onSubmit}>
          <Stack gap="lg">
            <Row gap="lg">
              <Field
                label={t('onboarding.about.firstNameLabel')}
                required
                style={{ flex: 1 }}
              >
                <Input
                  name="firstName"
                  defaultValue={profile?.firstName ?? ''}
                  required
                  maxLength={80}
                />
              </Field>
              <Field
                label={t('onboarding.about.lastNameLabel')}
                required
                style={{ flex: 1 }}
              >
                <Input
                  name="lastName"
                  defaultValue={profile?.lastName ?? ''}
                  required
                  maxLength={80}
                />
              </Field>
            </Row>
            <Field label={t('onboarding.about.industryLabel')}>
              <Select
                name="industry"
                placeholder={t('onboarding.about.industryPlaceholder')}
                defaultValue={profile?.industry ?? ''}
                options={INDUSTRY_OPTIONS}
              />
            </Field>
            <Field label={t('onboarding.about.jobTitleLabel')}>
              <Input
                name="jobTitle"
                placeholder={t('onboarding.about.jobTitlePlaceholder')}
                defaultValue={profile?.jobTitle ?? ''}
                maxLength={80}
              />
            </Field>
            <Field label={t('onboarding.about.locationLabel')}>
              <PlacePicker
                mode="region"
                value={place}
                onChange={setPlace}
                placeholder={t('onboarding.about.locationPlaceholder')}
              />
            </Field>
            <Checkbox
              checked={marketingOptIn}
              onCheckedChange={(v) => setMarketingOptIn(v === true)}
              label={
                // Regular weight: consent copy is a caption, not a call to
                // action (the primitive's default 600 is for form labels).
                <Body size="sm" color="mute">
                  {t('onboarding.about.marketingOptIn')}
                </Body>
              }
            />
            {error !== null ? (
              <Body size="sm" color="destructive">
                {error}
              </Body>
            ) : null}
            <Button
              variant="primary"
              type="submit"
              iconRight={I.arrow}
              loading={updateProfile.isPending}
              block
            >
              {t('onboarding.about.cta')}
            </Button>
          </Stack>
        </form>
        )}
      </Card>
    </EntryJourneyScreen>
  );
}
